// src/data/pickerNotifications.ts
// ------------------------------
// Repository for `request_picker_notifications` (db-165) — the ATOMIC dedup for the
// "Picker Order Confirmation" comment + email. A picker completing several SOs of the
// SAME request (a top-up's many stationery packs, or a normal request's children) used
// to flood the DFV Service: the "already sent" state lived in the request's notes JSON
// (read-modify-write → races). Now each completion CLAIMS the notification, and the
// UNIQUE (request_id, item_type) constraint (db-165) guarantees only ONE claim per
// request+type succeeds — so the comment + email fire exactly once. Typed via getTypedClient().

import { logger } from "../utils/logger";
import { getTypedClient } from "../utils/supabase";

// Claim the picker-notification for (requestId, itemType). Returns true ONLY when THIS
// call inserted the row (it's the first for this request+type) → the caller should send
// the comment + email. On a conflict (already claimed) it returns false. FAIL-CLOSED: on
// any error / no client it returns false, so a transient failure can never cause a
// DUPLICATE send (a missed courtesy email is preferable to spamming the service).
export async function claimPickerNotification(
  requestId: number | string,
  itemType: string,
  sentBy?: string | null
): Promise<boolean> {
  const c = getTypedClient();
  if (!c) return false;
  const { data, error } = await c
    .from("request_picker_notifications")
    .upsert(
      { request_id: requestId as number, item_type: itemType, sent_by: sentBy ?? null },
      { onConflict: "request_id,item_type", ignoreDuplicates: true }
    )
    .select("id");
  if (error) {
    logger.warn("claimPickerNotification failed:", error.message);
    return false;
  }
  return Array.isArray(data) && data.length > 0;
}
